import { monthlyHourlyRate } from "../utils/monthlySalary";
import EditablePayrollRow from "./EditablePayrollRow";
import { registerStyles } from "../utils/registerStyles";
import React, { useEffect, useRef, useState } from "react";
import { FaArrowLeft, FaCalendarAlt, FaClock, FaMoneyBill, FaSave, FaSearch } from "react-icons/fa";
import { addDoc, collection, deleteDoc, doc, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useCompany } from "../contexts/CompanyContext";

const emptyForm = { name: "", type: "hourly", amount: "", weeklyHours: "40", monthlyDivisor: "" };
const timestamp = value => value?.toMillis?.() ?? (Date.parse(value) || 0);

export default function AddSalaryPopup({ isOpen, onClose, onSave, companyRootUid }) {
  const { activeCompany } = useCompany();
  const companyId = companyRootUid || activeCompany?.companyAccountUid;
  return isOpen ? <SalaryPage key={companyId || "none"} companyId={companyId} onClose={onClose} onSave={onSave} /> : null;
}

function SalaryPage({ companyId, onClose, onSave }) {
  const [salaryTypes, setSalaryTypes] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(Boolean(companyId));
  const [loadError, setLoadError] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const nameInput = useRef(null);
  const formElement = useRef(null);
  const busy = useRef(false);

  useEffect(() => {
    if (!companyId) return;
    return onSnapshot(collection(db, "users", companyId, "salaryTypes"), snapshot => {
      setSalaryTypes(snapshot.docs.map(item => ({ ...item.data(), id: item.id }))
        .sort((a, b) => timestamp(b.createdAt) - timestamp(a.createdAt) || a.name?.localeCompare(b.name || "", "sv")));
      setLoading(false);
      setLoadError("");
    }, () => {
      setLoading(false);
      setLoadError("Kunde inte hämta lönerna. Öppna sidan igen för att försöka på nytt.");
    });
  }, [companyId]);

  const change = event => {
    setForm(previous => ({ ...previous, [event.target.name]: event.target.value }));
    setSuccess("");
    setError("");
  };

  const save = async event => {
    event.preventDefault();
    if (!companyId || busy.current || loading || loadError) return;
    const amount = Number(form.amount);
    if (!form.name.trim() || !form.amount.trim() || !Number.isFinite(amount) || amount < 0) {
      setError("Ange ett lönenamn och ett giltigt belopp.");
      return;
    }
    busy.current = true;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const data = { name: form.name.trim(), type: form.type, amount, weeklyHours: Number(form.weeklyHours || 40), monthlyDivisor: Number(form.monthlyDivisor || 0), updatedAt: new Date().toISOString() };
      let saved;
      if (editingId) {
        await updateDoc(doc(db, "users", companyId, "salaryTypes", editingId), data);
        saved = { ...data, id: editingId, action: "update" };
      } else {
        const created = { ...data, createdAt: data.updatedAt, createdBy: companyId };
        const reference = await addDoc(collection(db, "users", companyId, "salaryTypes"), created);
        saved = { ...created, id: reference.id, action: "create" };
      }
      setSalaryTypes(previous => [saved, ...previous.filter(item => item.id !== saved.id)]);
      setForm(emptyForm);
      setEditingId(null);
      setSuccess(saved.action === "create" ? "Lönen har skapats." : "Lönen har uppdaterats.");
      onSave?.(saved);
    } catch {
      setError("Kunde inte spara lönen. Försök igen.");
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };

  const remove = async salary => {
    if (!companyId || busy.current || !window.confirm(`Vill du ta bort lönen ”${salary.name}”?`)) return;
    busy.current = true;
    setDeletingId(salary.id);
    setError("");
    setSuccess("");
    try {
      await deleteDoc(doc(db, "users", companyId, "salaryTypes", salary.id));
      setSalaryTypes(previous => previous.filter(item => item.id !== salary.id));
      if (editingId === salary.id) { setEditingId(null); setForm(emptyForm); }
      setSuccess("Lönen har tagits bort.");
      onSave?.({ ...salary, action: "delete" });
    } catch {
      setError("Kunde inte ta bort lönen. Försök igen.");
    } finally {
      busy.current = false;
      setDeletingId(null);
    }
  };

  const disabled = saving || Boolean(deletingId);
  const visible = salaryTypes.filter(salary => (salary.name || "").toLocaleLowerCase("sv").includes(search.trim().toLocaleLowerCase("sv")) && (filter === "all" || salary.type === filter));

  return <section className="salary-page" aria-label="Lönetyper">
    <header className="salary-page-header">
      <button type="button" onClick={onClose} disabled={disabled} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", border: "1px solid #3b82f6", borderRadius: 8, color: "#2563eb", background: "white", cursor: "pointer", fontWeight: 700 }}><FaArrowLeft />Tillbaka till löner</button>
      <h1><FaMoneyBill aria-hidden="true" />Lönetyper</h1>
    </header>
    <div className="salary-page-body">
      {!companyId && <p role="alert" className="salary-error">Välj ett företag för att hantera löner.</p>}
      <section className="salary-form-section" aria-labelledby="salary-form-title">
        <div className="salary-section-heading">
          <h2 id="salary-form-title">{editingId ? "Redigera lön" : "Skapa ny lön"}</h2>
          {editingId && <button type="button" className="salary-button salary-button-secondary" disabled={disabled} onClick={() => { setEditingId(null); setForm(emptyForm); setError(""); setSuccess(""); }}>Skapa ny lön</button>}
        </div>
        <form ref={formElement} onSubmit={save}>
          <fieldset className="salary-fields" disabled={disabled || !companyId || loading || Boolean(loadError)}>
            <div><label htmlFor="salary-name">Lönenamn</label><input ref={nameInput} id="salary-name" name="name" value={form.name} onChange={change} placeholder="Ex. Timlön lager" required /></div>
            <fieldset className="salary-type"><legend>Typ av lön</legend><div>
              <label><input type="radio" name="type" value="hourly" checked={form.type === "hourly"} onChange={change} /><FaClock aria-hidden="true" />Timlön</label>
              <label><input type="radio" name="type" value="monthly" checked={form.type === "monthly"} onChange={change} /><FaCalendarAlt aria-hidden="true" />Månadslön</label>
            </div></fieldset>
            <div><label htmlFor="salary-amount">{form.type === "hourly" ? "Timlön (kr/timme)" : "Månadslön (kr/månad)"}</label><input id="salary-amount" type="number" name="amount" value={form.amount} onChange={change} placeholder="Ange belopp" min="0" step="0.01" required /></div>
          </fieldset>
          {form.type === "monthly" && <div className="salary-monthly-settings">
            <div><label htmlFor="salary-weekly-hours">Ordinarie veckoarbetstid (timmar)</label><input id="salary-weekly-hours" name="weeklyHours" type="number" min="0.01" max="168" step="0.01" value={form.weeklyHours} onChange={change} required disabled={disabled} /></div>
            <div><label htmlFor="salary-monthly-divisor">Avtalad timlönedelare (valfri)</label><input id="salary-monthly-divisor" name="monthlyDivisor" type="number" min="0.01" step="0.01" value={form.monthlyDivisor} onChange={change} placeholder="Enligt kollektivavtal" disabled={disabled} /></div>
            <div className="salary-hourly-preview"><span>Automatisk timlön</span><strong>{monthlyHourlyRate(form.amount, form).toLocaleString("sv-SE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr/timme</strong><small>{form.monthlyDivisor ? "Månadslön ÷ avtalad delare" : "Månadslön × 12 ÷ (veckoarbetstid × 52)"}</small></div>
          </div>}
          <div className="salary-form-actions"><button type="submit" className="salary-button salary-button-primary" disabled={disabled || !companyId || loading || Boolean(loadError)}><FaSave />{saving ? "Sparar…" : editingId ? "Uppdatera lön" : "Skapa lön"}</button></div>
        </form>
      </section>
      {error && <p className="salary-error" role="alert">{error}</p>}
      {success && <p className="salary-success" role="status">{success}</p>}
      <section aria-labelledby="salary-list-title">
        <div className="salary-section-heading"><h2 id="salary-list-title">Alla löner <span>({salaryTypes.length})</span></h2></div>
        <div className="salary-search"><div className="salary-search-input"><FaSearch aria-hidden="true" /><input aria-label="Sök lön" placeholder="Sök lön…" value={search} onChange={event => setSearch(event.target.value)} /></div><select aria-label="Filtrera löner" value={filter} onChange={event => setFilter(event.target.value)}><option value="all">Alla lönetyper</option><option value="hourly">Timlön</option><option value="monthly">Månadslön</option></select></div>
        <div className="salary-table-wrap"><table className="salary-table"><thead><tr><th scope="col">Lönenamn</th><th scope="col">Typ av lön</th><th scope="col">Belopp</th><th scope="col">Åtgärder</th></tr></thead><tbody>
          {visible.map(salary => <EditablePayrollRow key={salary.id} item={salary} companyId={companyId} collectionName="salaryTypes" disabled={disabled} onDelete={remove} onSaved={onSave} columns={[
            { value: salary.name, fields: [{ key: "name", label: "Lönenamn i raden", required: true }] },
            { value: salary.type === "hourly" ? "Timlön" : "Månadslön", fields: [{ key: "type", label: "Lönetyp i raden", options: [{ value: "hourly", label: "Timlön" }, { value: "monthly", label: "Månadslön" }] }] },
            { value: `${Number(salary.amount || 0).toLocaleString("sv-SE")} kr${salary.type === "hourly" ? "/timme" : "/månad"}`, fields: [{ key: "amount", label: "Lönebelopp i raden", type: "number", min: 0, required: true }, ...(salary.type === "monthly" ? [{ key: "weeklyHours", label: "Veckoarbetstid i raden", type: "number", min: 0.01, max: 168, defaultValue: 40, required: true }, { key: "monthlyDivisor", label: "Avtalad delare i raden", type: "number", min: 0, defaultValue: 0 }] : [])] },
          ]} />)}
          {loadError && <tr><td colSpan={4} role="alert" className="salary-empty">{loadError}</td></tr>}
          {loading && <tr><td colSpan={4} className="salary-empty">Laddar löner…</td></tr>}
          {!loading && !loadError && !visible.length && <tr><td colSpan={4} className="salary-empty">{salaryTypes.length ? "Inga löner matchar sökningen." : "Inga löner ännu. Skapa din första lön ovan."}</td></tr>}
        </tbody></table></div>
      </section>
    </div>
  </section>;
}

registerStyles("SalaryPage", `
.salary-monthly-settings { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 24px; margin-top: 20px; }
.salary-monthly-settings label { display: block; font-size: 13px; font-weight: 700; color: #4b5563; margin-bottom: 8px; }
.salary-hourly-preview { display: flex; flex-direction: column; gap: 8px; color: #1e40af; background: #eff6ff; padding: 12px; border-radius: 8px; }
.salary-hourly-preview small { color: #64748b; }
@media (max-width: 650px) { .salary-monthly-settings { grid-template-columns: 1fr; } }
.salary-page { width: 100%; min-width: 0; background: #f7f7f8; color: #111827; box-sizing: border-box; }
.salary-page * { box-sizing: border-box; }
.salary-page-header { display: flex; flex-direction: column; align-items: flex-start; gap: 20px; padding: 20px 12px 16px; }
.salary-page h1 { display: flex; align-items: center; gap: 12px; margin: 0; font-size: 24px; font-weight: 800; }
.salary-page h1 svg { color: #1677ff; }
.salary-page h2 { margin: 0; font-size: 16px; font-weight: 800; }
.salary-page h2 span { color: #6b7280; font-size: 14px; font-weight: 500; }
.salary-page-body { padding: 0 12px 16px; }
.salary-form-section { padding: 20px; margin-bottom: 24px; background: #fff; border: 1px solid #d1d5db; border-radius: 8px; }
.salary-section-heading { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
.salary-fields { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(240px, 1fr) minmax(180px, 1fr); gap: 24px; margin: 0; padding: 0; border: 0; min-width: 0; }
.salary-fields > div { min-width: 0; }
.salary-fields > div > label, .salary-type legend { display: block; margin-bottom: 8px; padding: 0; font-size: 13px; font-weight: 700; color: #4b5563; }
.salary-page input:not([type="radio"]), .salary-page select { width: 100%; min-width: 0; height: 40px; padding: 0 12px; border: 1px solid #d1d5db; border-radius: 8px; background: #fff; color: #111827; font: inherit; font-size: 14px; }
.salary-type { padding: 0; margin: 0; min-width: 0; border: 0; }
.salary-type > div { display: flex; align-items: center; min-height: 40px; gap: 16px; flex-wrap: wrap; }
.salary-type label { display: flex; align-items: center; gap: 8px; font-size: 14px; cursor: pointer; }
.salary-type input { margin: 0; width: 16px; height: 16px; accent-color: #1677ff; }
.salary-form-actions { display: flex; justify-content: flex-end; padding-top: 16px; margin-top: 20px; border-top: 1px solid #e5e7eb; }
.salary-button { display: inline-flex; justify-content: center; align-items: center; gap: 8px; min-height: 38px; padding: 0 16px; border: 1px solid transparent; border-radius: 8px; font: inherit; font-size: 14px; font-weight: 700; cursor: pointer; }
.salary-button-primary { background: #1677ff; border-color: #1677ff; color: #fff; }
.salary-button-secondary { background: #fff; border-color: #3b82f6; color: #2563eb; }
.salary-button-danger { background: #fff; border-color: #dc2626; color: #dc2626; }
.salary-button:disabled { opacity: .5; cursor: not-allowed; }
.salary-button:not(:disabled):hover { filter: brightness(.96); }
.salary-page button:focus-visible, .salary-page input:focus-visible, .salary-page select:focus-visible { outline: 2px solid #1677ff; outline-offset: 2px; }
.salary-search { display: grid; grid-template-columns: minmax(0, 1fr) minmax(180px, 240px); gap: 16px; margin-bottom: 12px; }
.salary-search-input { position: relative; }
.salary-search-input svg { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #6b7280; }
.salary-search-input input { padding-left: 38px !important; }
.salary-table-wrap { overflow-x: auto; border: 1px solid #d1d5db; background: #fff; }
.salary-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.salary-table th { padding: 10px 12px; background: #f3f4f6; color: #111827; font-weight: 800; text-align: left; border: 1px solid #d1d5db; }
.salary-table td { padding: 10px 12px; border: 1px solid #e5e7eb; overflow-wrap: anywhere; }
.salary-table th:last-child { text-align: center; }
.salary-name { font-weight: 700; }
.salary-amount { font-variant-numeric: tabular-nums; }
.salary-row-actions { display: flex; justify-content: center; gap: 8px; }
.salary-icon-button { padding: 0; min-height: 32px; width: 34px; }
.salary-table .salary-empty { padding: 32px 16px; text-align: center; color: #6b7280; }
.salary-error, .salary-success { padding: 12px; border-radius: 8px; margin: 0 0 20px; font-size: 14px; }
.salary-error { color: #b91c1c; background: #fef2f2; border: 1px solid #fecaca; }
.salary-success { color: #15803d; background: #dcfce7; border: 1px solid #16a34a; }
@media (max-width: 1000px) { .salary-fields { grid-template-columns: 1fr 1fr; } }
@media (max-width: 600px) { .salary-fields, .salary-search { grid-template-columns: 1fr; gap: 16px; } .salary-form-section { padding: 16px; } .salary-table { min-width: 520px; } }
`);
